import React from 'react';
import styled from 'styled-components';
import Section from './Section';
import Container from './Container';

// Import team member images
import asmaImage from '../assets/team/asam.jpg';
import esamImage from '../assets/team/esam.jpg';
import kittiImage from '../assets/team/kitti.jpg';
import bilalImage from '../assets/team/bilal.jpg';
import muhammadImage from '../assets/team/muhammad.jpg';
import omerImage from '../assets/team/omer.jpg';

interface TeamMemberProps {
  image: string;
  name: string;
  title: string;
  bio: string;
}

const TeamSection = styled(Section)`
  background-color: ${props => props.theme.colors.background};
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(to right, transparent, ${props => props.theme.colors.lightGray}, transparent);
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xxl};
`;

const Title = styled.h2`
  margin-bottom: ${props => props.theme.spacing.md};
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -${props => props.theme.spacing.sm};
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 3px;
    background-color: ${props => props.theme.colors.primary};
  }
`;

const Subtitle = styled.p`
  max-width: 700px;
  margin: 0 auto;
  font-size: ${props => props.theme.fontSizes.lg};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.fontSizes.md};
  }
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${props => props.theme.spacing.xl};
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const MemberCard = styled.div`
  background-color: ${props => props.theme.colors.light};
  border-radius: ${props => props.theme.borderRadius.md};
  box-shadow: ${props => props.theme.shadows.md};
  overflow: hidden;
  transition: ${props => props.theme.transitions.medium};
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: ${props => props.theme.shadows.lg};
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  padding-top: 75%; /* 4:3 Aspect Ratio */
  overflow: hidden;
`;

const MemberImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: ${props => props.theme.transitions.medium};
  
  ${MemberCard}:hover & {
    transform: scale(1.05);
  }
`;

const MemberInfo = styled.div`
  padding: ${props => props.theme.spacing.xl};
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const MemberName = styled.h3`
  font-size: ${props => props.theme.fontSizes.xl};
  margin-bottom: ${props => props.theme.spacing.xs};
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -${props => props.theme.spacing.xs};
    left: 0;
    width: 50px;
    height: 2px;
    background-color: ${props => props.theme.colors.primary};
  }
`;

const MemberTitle = styled.h4`
  font-size: ${props => props.theme.fontSizes.md};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.accent};
  margin-bottom: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.sm};
`;

const MemberBio = styled.p`
  font-size: ${props => props.theme.fontSizes.md};
  line-height: 1.6;
  flex-grow: 1;
  
  /* Display only the first 6 lines with an ellipsis */
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ReadMoreButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  font-weight: ${props => props.theme.fontWeights.semiBold};
  cursor: pointer;
  padding: ${props => props.theme.spacing.sm} 0;
  margin-top: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.md};
  display: flex;
  align-items: center;
  
  &:hover {
    color: ${props => props.theme.colors.secondary};
    text-decoration: underline;
  }
`;

const ModalBackground = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: ${props => (props.isOpen ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: ${props => props.theme.spacing.md};
`;

const ModalContent = styled.div`
  background-color: ${props => props.theme.colors.light};
  border-radius: ${props => props.theme.borderRadius.md};
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: ${props => props.theme.spacing.xl};
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${props => props.theme.spacing.md};
  right: ${props => props.theme.spacing.md};
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${props => props.theme.colors.dark};
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const ModalHeader = styled.div`
  display: flex;
  margin-bottom: ${props => props.theme.spacing.lg};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    flex-direction: column;
  }
`;

const ModalImageContainer = styled.div`
  flex: 0 0 200px;
  margin-right: ${props => props.theme.spacing.lg};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    margin-right: 0;
    margin-bottom: ${props => props.theme.spacing.md};
    flex: 0 0 auto;
    width: 100%;
    max-width: 250px;
    align-self: center;
  }
`;

const ModalImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: ${props => props.theme.borderRadius.md};
`;

const ModalInfo = styled.div`
  flex: 1;
`;

const ModalName = styled.h2`
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const ModalTitle = styled.h3`
  color: ${props => props.theme.colors.accent};
  margin-bottom: ${props => props.theme.spacing.md};
  font-weight: ${props => props.theme.fontWeights.medium};
`;

const ModalBio = styled.div`
  line-height: 1.8;
  font-size: ${props => props.theme.fontSizes.md};
  
  p {
    margin-bottom: ${props => props.theme.spacing.md};
  }
`;

const TeamMember: React.FC<TeamMemberProps> = ({ image, name, title, bio }) => {
  const [modalOpen, setModalOpen] = React.useState(false);

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  return (
    <>
      <MemberCard>
        <ImageContainer>
          <MemberImage src={image} alt={name} />
        </ImageContainer>
        <MemberInfo>
          <MemberName>{name}</MemberName>
          <MemberTitle>{title}</MemberTitle>
          <MemberBio>{bio}</MemberBio>
          <ReadMoreButton onClick={openModal}>Read More</ReadMoreButton>
        </MemberInfo>
      </MemberCard>

      <ModalBackground isOpen={modalOpen} onClick={closeModal}>
        <ModalContent onClick={e => e.stopPropagation()}>
          <CloseButton onClick={closeModal}>✕</CloseButton>
          <ModalHeader>
            <ModalImageContainer>
              <ModalImage src={image} alt={name} />
            </ModalImageContainer>
            <ModalInfo>
              <ModalName>{name}</ModalName>
              <ModalTitle>{title}</ModalTitle>
              <ModalBio>
                {bio.split('\n').map((paragraph, i) => (
                  <p key={i}>{paragraph}</p>
                ))}
              </ModalBio>
            </ModalInfo>
          </ModalHeader>
        </ModalContent>
      </ModalBackground>
    </>
  );
};

const Team: React.FC = () => {
  const teamMembers = [
    {
      image: asmaImage,
      name: 'Asma Ali',
      title: 'President',
      bio: `Asma is the President of Project Assist. With a passion for service and an unwavering commitment to making a difference, Asma has played a pivotal role in coordinating and supporting the COVID-19 food drive, which helped distribute food to vulnerable populations during the pandemic. This initiative was widely recognized in the Bay Area, including coverage in the San Francisco Chronicle for its positive impact on the local Muslim community and beyond.

In addition to the leadership role at Project Assist, Asma is an active member of the Executive Committee of the Yaseen Foundation, a prominent organization focused on community outreach, support, and development. Asma's work in both organizations highlights their dedication to improving lives through direct action, collaboration, and advocacy.

Asma is passionate about building bridges within diverse communities, empowering individuals, and fostering a spirit of collective responsibility.`
    },
    {
      image: esamImage,
      name: 'Esam (Sammy) Hussain',
      title: 'Director',
      bio: `Sammy is a Muslim born and raised here in the Bay Area, CA. He works as an apartment building landlord. He was the recipient of a Housing Hero Award by the San Mateo County Housing Authority and was also mentioned in the San Mateo Daily Journal as a Landlord for his work and compassion for his colleagues. Esam was recently voted to be a Board Member at Yaseen Foundation thanks to his knowledge and passion in serving his community. Esam enjoys doing volunteer work in various ways for his community. He's volunteered for almost 8 years now with Islamic Relief USA as well as doing youth sports coaching in flag football as well as other sports.

He is in charge of the housing department for Project Assist and is passionate about helping refugees and newcomers to our community.`
    },
    {
      image: kittiImage,
      name: 'Kitti Hussain',
      title: 'Director',
      bio: `Kitti has a background in Early Childhood Education and has worked with children ever since her arrival to the states in 2013. She is a stay at home mom with 2 children, which allows her to fully immerse herself in volunteer work. Recently, she became an Executive Committee member at Yaseen Foundation, where she works on different projects bringing the Muslim community together. Kitti has volunteered with Samaritan House, SPCA, NCEFT, Islamic Relief USA, and different schools in the past years.

Due to her resourceful nature, at Project Assist she is responsible for acquiring/accumulating the basic necessities for newcomer families and helping them find whatever they need.`
    },
    {
      image: bilalImage,
      name: 'Bilal Seadan',
      title: 'Director',
      bio: 'Bilal Seadan is an entrepreneur dedicated to serving people and creating a positive impact in the world. With a passion for community development and innovation, he actively contributes to initiatives that uplift and empower others. A proud parent and multilingual professional fluent in three languages, Bilal brings a diverse perspective and deep commitment to making a difference.'
    },
    {
      image: muhammadImage,
      name: 'Muhammad Jahangir Bhatti',
      title: 'Finance',
      bio: `Muhammad Bhatti is the Finance and G&A Officer at Project Assist. His journey in the non-profit sector began with volunteering in the Bay Area community at the Yaseen Foundation. From 2017 to 2020, Muhammad served on several committees, including those focused on community events and operations. During this time, he also played a pivotal role in streamlining Yaseen's G&A operations and bookkeeping processes.

During the COVID-19 years, Muhammad and a group of like-minded individuals came together to assist the community by providing essential supplies, particularly during periods of high need. Muhammad is passionate about problem-solving through systematic processes at both individual and organizational levels. He welcomes you to connect if you are seeking sustainable, long-term solutions to challenges through efficient systems and processes.`
    },
    {
      image: omerImage,
      name: 'Omer Siddiqui',
      title: 'Director',
      bio: `Omer has lived in the Bay area for over 10 years with his family and is originally from Canada. Omer is an executive in the biotech/pharma industry working on developing drugs for patients suffering from diseases with high unmet need. Omer has been involved in non-profit work through participation in various organizations. In addition to being on the elections committee at Yaseen foundation, Omer also is part of the leadership team for coordinating a multi-faith food drive each month serving the local community in conjunction with Second Harvest. He was inspired by his daughter who led the student muslim club at her local high school helping local youth stay involved in helping the community. As part of the founding team for Project Assist, Omer is looking to work with the team to support people in need and come up with solutions to help families become self-sustained and independent.`
    }
  ];

  return (
    <TeamSection id="team">
      <Container>
        <SectionHeader>
          <Title>Our Team</Title>
          <Subtitle>
            Meet the dedicated individuals who are working to make a difference in the Bay Area community.
          </Subtitle>
        </SectionHeader>

        <TeamGrid>
          {teamMembers.map((member, index) => (
            <TeamMember
              key={index}
              image={member.image}
              name={member.name}
              title={member.title}
              bio={member.bio}
            />
          ))}
        </TeamGrid>
      </Container>
    </TeamSection>
  );
};

export default Team; 